import { type ApolloError, gql, useMutation } from "@apollo/client";
import PQueue from "p-queue";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Transaction } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";
import { SEARCH_TRANSACTION_INVOICES } from "./search-invoices";

const mutationQueue = new PQueue({ concurrency: 1 });

type AddInvoiceToTransactionInput = {
  bankTxnIds: string[];
  invoiceIds: string[];
};

export const useAddInvoiceToTransaction = (): [
  (input: AddInvoiceToTransactionInput) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [addInvoiceToTransaction, { loading, error }] = useMutation<{
    addInvoiceToTransaction: Transaction[];
  }>(ADD_INVOICE_TO_TRANSACTION_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS, SEARCH_TRANSACTION_INVOICES],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (!data?.addInvoiceToTransaction) return;
      cache.evict({ fieldName: "transactions" });
      cache.evict({ fieldName: "searchTransactionInvoices" });
      cache.gc();
    },
  });

  const handleAddInvoiceToTransaction = async (
    input: AddInvoiceToTransactionInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const result = await mutationQueue.add(async () => {
        const { data: response } = await addInvoiceToTransaction({
          variables: {
            bankTxnIds: input.bankTxnIds,
            invoiceIds: input.invoiceIds,
          },
        });

        if (!response) {
          throw new Error("No response from addInvoiceToTransaction");
        }

        toast.dismiss(toastId);

        return response.addInvoiceToTransaction;
      });

      return result || [];
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleAddInvoiceToTransaction, { loading, error }];
};

export const ADD_INVOICE_TO_TRANSACTION_MUTATION = gql`
  mutation AddInvoiceToTransaction(
    $bankTxnIds: [String!]!
    $invoiceIds: [String]!
  ) {
    addInvoiceToTransaction(bankTxnIds: $bankTxnIds, invoiceIds: $invoiceIds) {
      bankTxnId
      tenantId
      status
      amount
      currency
      createdAt
      counterpartyName
      counterpartyIban
      purpose
      executionDate
      smbAttachmentStatus
      side
      invoices {
        invoiceId
        invoiceNo
        totalAmount
        currency
        invoiceDate
      }
    }
  }
`;
